import React, { useEffect, useMemo, useState } from 'react'

import {
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Modal,
    Paper,
    Select,
    Slide,
    useTheme,
} from '@material-ui/core'
import { Pagination } from '@material-ui/lab'
import { toast } from 'react-toastify'

import { Container } from '../../../../components'
import { ChangeOrderRow } from '../../../../components/ChangeOrder/ChangeOrderRow'
import { axiosInstance } from '../../../../helpers'
import { useAppDispatch, usePagination, useUser } from '../../../../hooks'
import { changeOrderStatusOptions } from '../../../../hooks/useChangeOrder'
import {
    AreaConfig,
    BaseWorkorder,
    ChangeOrder,
    ChangeOrderConfig,
    ChangeOrderStatus,
    convertListToMap,
    createWorkorderFromResponse,
    ListVendor,
    ModelMap,
    Schedule,
    Service,
} from '../../../../models'
import {
    updateChangeOrderStatus,
    updateStateWorkorder,
} from '../../../../store'
import { useMultiSelectStyles } from '../../../../styles'
import { typographyStyle } from '../../../../theme/typographyStyle'

export const CO_WO_RANGE_KEY = 'CO_WO_RANGE_KEY'

export interface ServiceAreaChangeState {
    [areaId: number]: { initial: boolean; current: boolean; label: string }
}

interface Props {
    areaConfigMap: ModelMap<AreaConfig>
    workorder?: BaseWorkorder
    changeOrderConfigList?: ChangeOrderConfig[]
    vendorList: ListVendor[]
    serviceList: Service[]
    schedule?: Schedule
    onClose: () => void
}

export const ChangeOrderModal = (props: Props) => {
    const { workorder, onClose, changeOrderConfigList } = props

    const theme = useTheme()
    const dispatch = useAppDispatch()
    const classes = useMultiSelectStyles()
    const { workspaceUser } = useUser()

    const open = workorder !== undefined

    const [changeOrders, setChangeOrders] = useState<ChangeOrder[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [statusFilter, setStatusFilter] = useState<ChangeOrderStatus>(
        ChangeOrderStatus.PENDING,
    )

    useEffect(() => {
        setChangeOrders(
            workorder
                ? withWorkorderLocation(
                      workorder.changeorder_set ?? [],
                      workorder,
                  )
                : [],
        )
        setStatusFilter(ChangeOrderStatus.PENDING)
    }, [workorder])

    const changeOrderConfigMap = convertListToMap(changeOrderConfigList ?? [])

    const filteredChangeOrders = useMemo(() => {
        return changeOrders.filter((co) => co.status === statusFilter)
    }, [changeOrders, statusFilter])

    const { page, setPage, pageData, numPages, start, end } = usePagination(
        filteredChangeOrders,
    )

    const updateLocalChangeOrder = (updated: ChangeOrder) => {
        setChangeOrders((prev) =>
            prev.map((co) => (co.id === updated.id ? updated : co)),
        )
    }

    const removeLocalChangeOrder = (removed: ChangeOrder) => {
        setChangeOrders((prev) => prev.filter((co) => co.id !== removed.id))
    }

    const syncWorkorderChangeOrders = (nextChangeOrders: ChangeOrder[]) => {
        if (!workorder) {
            return
        }
        dispatch(
            updateStateWorkorder({
                ...workorder,
                changeorder_set: nextChangeOrders,
            }),
        )
    }

    const approveDenyChangeOrder = async (
        changeOrder: ChangeOrder,
        status: ChangeOrderStatus,
    ) => {
        try {
            const response = await axiosInstance.post(
                `workorder/change-order/${changeOrder.id}/change_status/`,
                { status },
            )

            if (response.data.updated_workorder) {
                const updatedWorkorder = createWorkorderFromResponse(
                    response.data.updated_workorder,
                )
                let newWorkorder: BaseWorkorder | undefined
                if (response.data.new_workorder) {
                    newWorkorder = createWorkorderFromResponse(
                        response.data.new_workorder,
                    )
                }
                dispatch(
                    updateChangeOrderStatus(updatedWorkorder, newWorkorder),
                )
                setChangeOrders(
                    withWorkorderLocation(
                        updatedWorkorder.changeorder_set ?? [],
                        updatedWorkorder,
                    ),
                )
            } else {
                const returnedChangeOrder: ChangeOrder =
                    response.data.change_order
                const next: ChangeOrder = {
                    ...changeOrder,
                    status: returnedChangeOrder.status,
                    handled_by: returnedChangeOrder.handled_by,
                    handled_at: returnedChangeOrder.handled_at,
                    created_by:
                        returnedChangeOrder.created_by ??
                        changeOrder.created_by,
                }
                updateLocalChangeOrder(next)
                syncWorkorderChangeOrders(
                    changeOrders.map((co) => (co.id === next.id ? next : co)),
                )
            }

            toast.success('Change Order updated successfully')
        } catch (error) {
            toast.error('Error updating Change Order')
        }
    }

    const deleteChangeOrder = async (changeOrder: ChangeOrder) => {
        try {
            setIsLoading(true)
            await axiosInstance.delete(
                `workorder/change-order/${changeOrder.id}/`,
            )
            const next = changeOrders.filter((co) => co.id !== changeOrder.id)
            removeLocalChangeOrder(changeOrder)
            syncWorkorderChangeOrders(next)
            toast.success('Change Order deleted successfully!')
        } catch (error) {
            const data = (error as {
                response?: { data?: { key?: string; message?: string } }
            })?.response?.data
            toast.error(
                data?.key === 'CHANGE_ORDER_LOCKED'
                    ? 'Cannot delete: work order is approved or invoiced'
                    : data?.message ?? 'Error deleting change order',
            )
            throw error
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <Modal
            open={open}
            onClose={onClose}
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Slide direction="up" in={open}>
                <Paper
                    style={{
                        width: '80vw',
                        height: '80vh',
                        maxWidth: 'calc(100vw - 24px)',
                        maxHeight: 'calc(100vh - 24px)',
                        outline: 'none',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Container
                        style={{
                            borderRadius: theme.shape.borderRadius,
                            width: '100%',
                            height: '100%',
                            minHeight: 0,
                            flexDirection: 'column',
                        }}
                    >
                        <Container
                            style={{
                                borderBottom: `1px solid ${theme.palette.grey[800]}`,
                                height: HEADER_H,
                                flexShrink: 0,
                                padding: theme.spacing(0, 1),
                                alignItems: 'center',
                            }}
                        >
                            <span
                                style={{
                                    ...typographyStyle(theme.typography.h5),
                                    fontWeight: 700,
                                    color: theme.palette.grey[800],
                                }}
                            >
                                Change Orders
                                {workorder?.unit_name
                                    ? ` · ${workorder.unit_name}`
                                    : ''}
                            </span>
                        </Container>
                        <Container
                            style={{
                                padding: theme.spacing(1),
                                flexShrink: 0,
                                flexWrap: 'wrap',
                                alignItems: 'center',
                            }}
                        >
                            <FormControl
                                className={classes.formControl}
                                variant="outlined"
                            >
                                <InputLabel id="WO_CO_STATUS_FILTER_LABEL">
                                    Status
                                </InputLabel>
                                <Select
                                    labelId="WO_CO_STATUS_FILTER_LABEL"
                                    id="wo_co_status_filter"
                                    onChange={(
                                        event: React.ChangeEvent<{
                                            value: unknown
                                        }>,
                                    ) => {
                                        setStatusFilter(
                                            event.target
                                                .value as ChangeOrderStatus,
                                        )
                                        setPage(1)
                                    }}
                                    value={statusFilter}
                                    label="Status"
                                    style={{
                                        width: '200px',
                                        height: '40px',
                                    }}
                                >
                                    {changeOrderStatusOptions.map((option) => {
                                        return (
                                            <MenuItem
                                                key={`WO_CO_STATUS_FILTER_${option}`}
                                                value={option}
                                            >
                                                {option}
                                            </MenuItem>
                                        )
                                    })}
                                </Select>
                            </FormControl>
                        </Container>
                        {isLoading ? (
                            <Container
                                style={{
                                    flex: 1,
                                    minHeight: 0,
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                }}
                            >
                                <CircularProgress size={100} />
                            </Container>
                        ) : (
                            <Container
                                style={{
                                    flexDirection: 'column',
                                    flex: 1,
                                    minHeight: 0,
                                    overflow: 'auto',
                                }}
                            >
                                {pageData.length === 0 ? (
                                    <Container
                                        style={{
                                            flex: 1,
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <span
                                            style={{
                                                ...typographyStyle(
                                                    theme.typography.h6,
                                                ),
                                                color:
                                                    theme.palette.text
                                                        .secondary,
                                            }}
                                        >
                                            No Change Orders
                                        </span>
                                    </Container>
                                ) : (
                                    pageData.map((changeOrder) => {
                                        const changeOrderConfig =
                                            changeOrderConfigMap[
                                                changeOrder.change_order_config
                                            ]

                                        return (
                                            <ChangeOrderRow
                                                key={`WO_CHANGE_ORDER_ROW_${changeOrder.id}`}
                                                changeOrder={changeOrder}
                                                theme={theme}
                                                changeOrderConfig={
                                                    changeOrderConfig
                                                }
                                                approveDenyChangeOrder={
                                                    approveDenyChangeOrder
                                                }
                                                workspaceUser={workspaceUser}
                                                deleteChangeOrder={
                                                    deleteChangeOrder
                                                }
                                                parentWorkorder={workorder}
                                            />
                                        )
                                    })
                                )}
                            </Container>
                        )}
                        <Container
                            style={{
                                borderTop: `1px solid ${theme.palette.grey[400]}`,
                                padding: theme.spacing(1),
                                justifyContent: 'center',
                                alignItems: 'center',
                                flexShrink: 0,
                                flexWrap: 'wrap',
                            }}
                        >
                            <Pagination
                                count={numPages}
                                page={page}
                                onChange={(_: unknown, newPage: number) => {
                                    setPage(newPage)
                                }}
                            />
                            <span>
                                {start} - {end} of {filteredChangeOrders.length}
                            </span>
                        </Container>
                    </Container>
                </Paper>
            </Slide>
        </Modal>
    )
}

const HEADER_H = 50

const withWorkorderLocation = (
    changeOrders: ChangeOrder[],
    workorder: BaseWorkorder,
): ChangeOrder[] => {
    const locationFromWorkorder = {
        unit_name: workorder.unit_name,
        folder_name: workorder.folder_name,
        path: workorder.path,
    }
    const vendorFromWorkorder =
        workorder.vendor_id != null
            ? {
                  id: workorder.vendor_id,
                  name: workorder.vendor_name ?? '',
              }
            : undefined

    return changeOrders.map((co) => ({
        ...co,
        location: {
            unit_name:
                co.location?.unit_name ?? locationFromWorkorder.unit_name,
            folder_name:
                co.location?.folder_name ?? locationFromWorkorder.folder_name,
            path: co.location?.path ?? locationFromWorkorder.path,
        },
        vendor: co.vendor ?? vendorFromWorkorder,
        workorder: co.workorder ?? {
            id: workorder.id,
            status: workorder.status,
        },
    }))
}
